/**
 * DigiLockerCustomerWidget — Staff-initiated DigiLocker request panel.
 * Lives on CustomerDetailPage. Generates a one-time consent link that
 * staff can WhatsApp / SMS / Email to the customer.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ShieldCheck, Link2, Copy, MessageCircle, Mail, Send,
  CheckCircle2, Loader2, FileText, Sparkles, ExternalLink, Clock,
} from "lucide-react";
import { toast } from "sonner";

const API = `${window.location.origin}/api/digilocker`;

export default function DigiLockerCustomerWidget({ customerId, customerName, customerPhone, customerEmail }) {
  const [generating, setGenerating] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [links, setLinks] = useState([]);
  const [pending, setPending] = useState([]);
  const [status, setStatus] = useState({ is_mock: true });

  const loadLinks = useCallback(async () => {
    try {
      const [linksResp, statusResp] = await Promise.all([
        axios.get(`${API}/customer/${customerId}/links`, { withCredentials: true }),
        axios.get(`${API}/status`),
      ]);
      setLinks(linksResp.data.links || []);
      setPending(linksResp.data.pending_sessions || []);
      setStatus(statusResp.data);
    } catch { /* silent */ }
  }, [customerId]);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const generateLink = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/request-link`, {
        customer_id: customerId,
        origin_url: window.location.origin,
        purpose: "Periodic CDD refresh",
      }, { withCredentials: true });
      setShareUrl(data.share_url);
      toast.success("Consent link generated — share with customer");
      loadLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate link");
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard");
  };

  const waMessage = encodeURIComponent(
    `Hi ${customerName || "there"}, please complete your KYC by sharing your documents via DigiLocker (Govt of India). It takes 30 seconds:\n\n${shareUrl}\n\nThis link is valid for 24 hours.`
  );
  const emailSubject = encodeURIComponent("Complete your KYC via DigiLocker");
  const emailBody = encodeURIComponent(
    `Hi ${customerName || "there"},\n\nPlease complete your KYC verification by securely sharing your documents from DigiLocker (Government of India).\n\nClick the link below to begin (takes 30 seconds):\n${shareUrl}\n\nThis link is valid for 24 hours.\n\nRegards,\nRudrik Compliance Team`
  );

  return (
    <div data-testid="digilocker-customer-widget" className="card-aml" style={{ marginBottom: 20 }}>
      <div className="flex items-center justify-between mb-3" style={{ gap: 12 }}>
        <div className="flex items-center gap-2">
          <div style={{ background: "rgba(37,99,235,0.15)", width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ShieldCheck size={16} style={{ color: "#3b82f6" }} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>DigiLocker Verification</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              {status.is_mock ? "Sandbox Mode · " : "Live · "}
              Aadhaar / PAN / DL / Voter ID
            </div>
          </div>
        </div>
        {!shareUrl && (
          <button
            onClick={generateLink}
            disabled={generating}
            data-testid="dl-generate-link-btn"
            style={{
              background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "#fff", border: "none",
              padding: "9px 16px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: generating ? "wait" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            {generating ? <Loader2 className="animate-spin" size={13} /> : <Link2 size={13} />}
            Generate Link
          </button>
        )}
      </div>

      {shareUrl && (
        <div data-testid="dl-share-panel" style={{ background: "#080c12", border: "1px solid #2563eb55", borderRadius: 8, padding: 14, marginBottom: 12 }}>
          <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 6 }}>
            Share this link with the customer
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            <code style={{
              flex: 1, background: "#0d1117", border: "1px solid #1e2530", borderRadius: 6,
              padding: "8px 10px", color: "#cbd5e1", fontSize: 11, fontFamily: "ui-monospace, monospace",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{shareUrl}</code>
            <button onClick={copyLink} data-testid="dl-copy-btn" style={iconBtnStyle}><Copy size={13} /></button>
            <a href={shareUrl} target="_blank" rel="noopener noreferrer" style={{ ...iconBtnStyle, textDecoration: "none" }}>
              <ExternalLink size={13} />
            </a>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {customerPhone && (
              <a
                href={`https://wa.me/${customerPhone.replace(/\D/g, "")}?text=${waMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="dl-send-wa-btn"
                style={shareBtnStyle("#22c55e")}
              >
                <MessageCircle size={12} /> WhatsApp
              </a>
            )}
            {customerEmail && (
              <a
                href={`mailto:${customerEmail}?subject=${emailSubject}&body=${emailBody}`}
                data-testid="dl-send-email-btn"
                style={shareBtnStyle("#3b82f6")}
              >
                <Mail size={12} /> Email
              </a>
            )}
            {customerPhone && (
              <a
                href={`sms:${customerPhone}?body=${waMessage}`}
                data-testid="dl-send-sms-btn"
                style={shareBtnStyle("#a855f7")}
              >
                <Send size={12} /> SMS
              </a>
            )}
          </div>
          <div style={{ marginTop: 10, fontSize: 10, color: "#64748b", display: "flex", alignItems: "center", gap: 5 }}>
            <Clock size={10} /> Link expires in 24 hours · Single use
          </div>
        </div>
      )}

      {/* Pending requests */}
      {pending.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 6 }}>
            Pending ({pending.length})
          </div>
          {pending.map((p) => (
            <div key={p.id} data-testid={`dl-pending-${p.id}`} style={{ background: "#080c12", border: "1px solid #f59e0b33", borderRadius: 6, padding: "8px 12px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#f59e0b" }}>Awaiting customer consent</div>
                <div style={{ fontSize: 10, color: "#94a3b8" }}>{p.purpose} · sent {new Date(p.created_at).toLocaleString()}</div>
              </div>
              <Clock size={12} style={{ color: "#f59e0b" }} />
            </div>
          ))}
        </div>
      )}

      {/* Past successful links */}
      {links.length > 0 && (
        <div>
          <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 6 }}>
            Verified Pulls ({links.length})
          </div>
          {links.slice(0, 3).map((l) => (
            <div key={l.id} data-testid={`dl-link-${l.id}`} style={{ background: "#080c12", border: "1px solid #22c55e33", borderRadius: 6, padding: "10px 12px", marginBottom: 6 }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={13} style={{ color: "#22c55e" }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#f1f5f9" }}>
                      {l.profile?.name || "Verified"}
                      {l.mode === "mock" && (
                        <span style={{ marginLeft: 6, background: "rgba(245,158,11,0.15)", color: "#f59e0b", border: "1px solid #f59e0b55", fontSize: 8, padding: "2px 6px", borderRadius: 3, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>Mock</span>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: "#94a3b8" }}>
                      {l.documents?.length || 0} docs · {new Date(l.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                {l.documents?.length > 0 && (
                  <div style={{ display: "flex", gap: 4 }}>
                    {l.documents.map((d) => (
                      <span key={d.uri} title={d.description} style={{ background: "rgba(34,197,94,0.12)", color: "#22c55e", border: "1px solid #22c55e55", fontSize: 8, padding: "2px 5px", borderRadius: 3, fontWeight: 700, letterSpacing: 0.4 }}>
                        {d.doctype}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!shareUrl && pending.length === 0 && links.length === 0 && (
        <div style={{ padding: 14, background: "#080c12", border: "1px dashed #1e2530", borderRadius: 8, color: "#64748b", fontSize: 11, display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Sparkles size={12} style={{ color: "#3b82f6", marginTop: 2, flexShrink: 0 }} />
          <div>Generate a secure DigiLocker consent link to pull this customer's Aadhaar, PAN, Driving Licence, and Voter ID — directly from gov.in. Customer clicks the link, signs in, and grants consent. No paperwork.</div>
        </div>
      )}
    </div>
  );
}

const iconBtnStyle = {
  background: "transparent", border: "1px solid #1e2530", borderRadius: 6,
  width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center",
  color: "#94a3b8", cursor: "pointer",
};

const shareBtnStyle = (color) => ({
  background: `${color}18`, color, border: `1px solid ${color}55`,
  padding: "6px 12px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 5, textDecoration: "none",
});
